import { buttonVariants } from "@rallly/ui";
import { Button } from "@rallly/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@rallly/ui/dialog";

import { ArrowUpRightIcon, Share2Icon } from "lucide-react";
import Link from "next/link";
import React from "react";
import { useCopyToClipboard } from "react-use";

import { usePoll } from "@/features/poll/client";
import { Trans } from "@/i18n/client";

export function CopyInviteLinkButton() {
  const [didCopy, setDidCopy] = React.useState(false);
  const [state, copyToClipboard] = useCopyToClipboard();
  const poll = usePoll();
  const inviteLinkWithoutProtocol = poll.inviteLink.replace(/^https?:\/\//, "");

  React.useEffect(() => {
    if (state.error) {
      console.error(`Unable to copy value: ${state.error.message}`);
    }
  }, [state]);

  return (
    <Button
      className="min-w-0 grow"
      onClick={() => {
        copyToClipboard(poll.inviteLink);
        setDidCopy(true);
        setTimeout(() => {
          setDidCopy(false);
        }, 1000);
      }}
    >
      {didCopy ? (
        <Trans i18nKey="copied" />
      ) : (
        <span className="min-w-0 truncate">{inviteLinkWithoutProtocol}</span>
      )}
    </Button>
  );
}

export const InviteDialog = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const poll = usePoll();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger render={<Button variant="outline" />}>
        <Share2Icon data-icon="inline-start" />
        <span className="sr-only sm:not-sr-only">
          <Trans i18nKey="share" defaults="Share" />
        </span>
      </DialogTrigger>
      <DialogContent size="lg" data-testid="invite-participant-dialog">
        <div className="flex">
          <Share2Icon className="size-6 text-primary" />
        </div>
        <DialogHeader className="">
          <DialogTitle>
            <Trans i18nKey="share" defaults="Share" />
          </DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey="inviteParticipantsDescription"
              defaults="Copy and share the invite link to start gathering responses from your participants."
            />
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0">
          <p className="mb-2 text-sm">
            <Trans i18nKey="inviteLink" defaults="Invite link" />
          </p>
          <div className="flex gap-2">
            <CopyInviteLinkButton />
            <div className="shrink-0">
              <Link
                target="_blank"
                href={`/invite/${poll.id}`}
                prefetch={false}
                className={buttonVariants()}
              >
                <ArrowUpRightIcon className="size-4" />
              </Link>
            </div>
          </div>
        </div>
        <p className="text-muted-foreground text-sm">
          <Trans
            i18nKey="inviteParticipantLinkInfo"
            defaults="Anyone with this link will be able to vote on your poll."
          />
        </p>
      </DialogContent>
    </Dialog>
  );
};
